import { ChangeEventHandler, MouseEventHandler, useRef } from 'react'

import clsx from 'clsx'

import Button from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconMark from '@/components/view/atoms/icon/IconMark'
import Text from '@/components/view/atoms/text/Text'
import Image from '@/components/view/utils/image/Image'

export const UploadDropZoneImageSize = {
  SMALL: 'SMALL',
  LARGE: 'LARGE',
  BANNER: 'BANNER'
} as const

export type UploadDropZoneImageSize =
  (typeof UploadDropZoneImageSize)[keyof typeof UploadDropZoneImageSize]
interface Props {
  buttonText: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onDelete?(): void
  fileName?: string
  filePath?: string
  fromUrl?: boolean
  dragging?: boolean
  onlyImageFile?: boolean
  imageSize?: UploadDropZoneImageSize
  disabled?: boolean
}

const UploadDropZone = ({
  buttonText,
  onClick,
  onChange,
  onDelete,
  fileName,
  filePath,
  fromUrl,
  dragging,
  onlyImageFile,
  imageSize = UploadDropZoneImageSize.SMALL,
  disabled = false
}: Props) => {
  const ref = useRef<HTMLInputElement>(null)

  const widthClass =
    imageSize == UploadDropZoneImageSize.SMALL
      ? 'w-10'
      : imageSize == UploadDropZoneImageSize.LARGE
        ? 'w-[104px]'
        : UploadDropZoneImageSize.BANNER
          ? 'w-[156px]'
          : ''

  const ChangeButton = () => (
    <Button
      color={'secondary'}
      onClick={(e) => {
        onChange ? ref.current?.click() : onClick ? onClick(e) : undefined
      }}
      disabled={disabled}
    >
      {fileName ? 'ファイルを変更する' : buttonText}
    </Button>
  )

  return (
    <div
      className={clsx(
        `relative flex w-full items-center gap-x-2 rounded-lg border`,
        dragging
          ? 'border-input bg-surface-secondary min-h-[120px] justify-center'
          : fileName
            ? 'bg-fill-primary border-primary flex min-h-20 w-full flex-nowrap items-center justify-between gap-x-2 rounded-lg border py-2 pr-6 pl-2'
            : 'bg-surface-secondary min-h-[120px] justify-center border-dashed'
      )}
    >
      {dragging ? (
        <div className='grid place-content-center'>
          <Text weight='bold'>アップロードするファイルをドロップする</Text>
        </div>
      ) : fileName ? (
        <>
          <div className='flex items-center gap-2'>
            {filePath ? (
              <div
                className={clsx(
                  widthClass,
                  imageSize == UploadDropZoneImageSize.BANNER ? 'aspect-3/2' : 'aspect-square'
                )}
              >
                <Image src={filePath} width={'100%'} height={'100%'} fitCover radius={0} />
              </div>
            ) : (
              <IconMark type={IconType.FILE} />
            )}
            <Text>{fileName}</Text>
          </div>
          <div className='flex items-center gap-2'>
            <ChangeButton />
            {onDelete && (
              <IconButton
                type={IconType.DELETE}
                line
                color={IconColor.SECONDARY}
                onClick={() => onDelete()}
                disabled={disabled}
              />
            )}
          </div>
        </>
      ) : (
        <ChangeButton />
      )}
      <input
        type='file'
        className='sr-only absolute inset-0 h-full w-full'
        hidden
        multiple
        ref={ref}
        onChange={onChange}
        accept={onlyImageFile ? 'image/*' : undefined}
        value={''} // for reloading in reuploading same name file
      />
      {!dragging && fromUrl && (
        <TextButton size={12} onClick={onClick}>
          URLから追加
        </TextButton>
      )}
    </div>
  )
}

export default UploadDropZone
